import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@tolgee/ngx';

/**
 * Enum for slot unavailability reasons
 * Defined locally to avoid circular dependency with temp-services
 * This matches the ESlotReason enum from temp-services but is kept separate to avoid circular dependencies
 */
export enum SlotUnavailableReason {
  TIME_PAST = 'time_past',
  MIN_NOTICE = 'min_notice',
  BOOKED = 'booked',
  BUFFER = 'buffer',
  BUSY = 'busy',
  DURATION = 'duration',
}

/**
 * Pipe for translating unavailable reason codes to human-readable text
 * Uses explicit translation keys for Tolgee
 *
 * Examples:
 * - {{ SlotUnavailableReason.BOOKED | unavailableReasonTranslate }} -> "Booked"
 * - {{ SlotUnavailableReason.BOOKED | unavailableReasonTranslate:true }} -> "This slot is already booked."
 */
@Pipe({
  name: 'unavailableReasonTranslate',
  pure: false,
})
export class UnavailableReasonTranslatePipe implements PipeTransform {
  private translateService = inject(TranslateService);

  // Simple cache
  private cache = new Map<string, string>();

  /**
   * Transform a reason code into its translation
   * @param reason The unavailable reason code from SlotUnavailableReason
   * @param isText Whether to get the detailed text version
   * @returns The translated string
   */
  transform(reason: SlotUnavailableReason | string, isText = false): string {
    // Generate cache key including language
    const cacheKey = `${reason}_${isText ? 'text' : 'title'}_${this.translateService.language}`;

    // Check cache first for better performance
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) || '';
    }

    // Get translation directly based on reason and isText flag
    let result = '';

    if (isText) {
      // Detailed text translations
      switch (reason) {
        case SlotUnavailableReason.TIME_PAST:
          result = this.translateService.instant('booking.unavailable.time_past_text');
          break;
        case SlotUnavailableReason.MIN_NOTICE:
          result = this.translateService.instant('booking.unavailable.min_notice_text');
          break;
        case SlotUnavailableReason.BOOKED:
          result = this.translateService.instant('booking.unavailable.booked_text');
          break;
        case SlotUnavailableReason.BUFFER:
          result = this.translateService.instant('booking.unavailable.buffer_text');
          break;
        case SlotUnavailableReason.DURATION:
          result = this.translateService.instant('booking.unavailable.duration_text');
          break;
        case SlotUnavailableReason.BUSY:
          result = this.translateService.instant('booking.unavailable.busy_text');
          break;
        default:
          result = '';
      }
    } else {
      // Title translations
      switch (reason) {
        case SlotUnavailableReason.TIME_PAST:
          result = this.translateService.instant('booking.unavailable.time_past');
          break;
        case SlotUnavailableReason.MIN_NOTICE:
          result = this.translateService.instant('booking.unavailable.min_notice');
          break;
        case SlotUnavailableReason.BOOKED:
          result = this.translateService.instant('booking.unavailable.booked');
          break;
        case SlotUnavailableReason.BUFFER:
          result = this.translateService.instant('booking.unavailable.buffer');
          break;
        case SlotUnavailableReason.DURATION:
          result = this.translateService.instant('booking.unavailable.duration');
          break;
        case SlotUnavailableReason.BUSY:
          result = this.translateService.instant('booking.unavailable.busy');
          break;
        default:
          result = '';
      }
    }

    // Cache the result
    this.cache.set(cacheKey, result);

    // If cache gets too large, clear older entries
    if (this.cache.size > 5) {
      this.cleanCache();
    }

    return result;
  }

  private cleanCache(): void {
    // Keep only the most recent entries
    const entriesToKeep = Array.from(this.cache.entries()).slice(-3);

    this.cache.clear();

    entriesToKeep.forEach(([key, value]) => {
      this.cache.set(key, value);
    });
  }
}
